const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

export function capitalizeFirstLetter(str) {
  if (typeof str !== 'string' || str.length === 0) {
    return ""; // Handle empty or non-string inputs
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Card patterns for payment processor detection
export const creditCardPatterns = {
  visa: /^4[0-9]{12}(?:[0-9]{3})?(?:[0-9]{3})?$/, // 13, 16, or 19 digits
  mastercard: /^5[1-5][0-9]{14}$/,                // 16 digits
  amex: /^3[47][0-9]{13}$/,                       // 15 digits
  discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,      // 16 digits
  jcb: /^(?:2131|1800|35\d{3})\d{11}$/,           // 15 or 16 digits
  diners: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,     // 14 digits
}

// A comprehensive object defining various card types, their patterns, lengths, and CVV code lengths.
const cardTypes = {
  visa: {
    type: 'visa',
    patterns: [/^4/],
    lengths: [13, 16, 19],
    cvvLength: 3,
  },
  mastercard: {
    type: 'mastercard',
    patterns: [/^5[1-5]/, /^2(2(2[1-9]|[3-9])|[3-6]|7([0-1]|20))/], // Covers 51-55 and the newer 2221-2720 ranges.
    lengths: [16],
    cvvLength: 3,
  },
  amex: {
    type: 'amex',
    patterns: [/^3[47]/],
    lengths: [15],
    cvvLength: 4,
  },
  discover: {
    type: 'discover',
    // Covers 6011, 644-649, and 65.
    patterns: [/^6(011|4[4-9]|5)/],
    lengths: [16, 19],
    cvvLength: 3,
  },
  diners: {
    type: 'diners',
    patterns: [/^3(0[0-5]|[689])/], // Covers 300-305, 36, 38-39. Note: 54/55-prefixed cards are now processed as Mastercards.
    lengths: [14, 16, 19],
    cvvLength: 3,
  },
  jcb: {
    type: 'jcb',
    patterns: [/^35(2[89]|[3-8])/],
    lengths: [16, 17, 18, 19],
    cvvLength: 3,
  },
};

export const getCardInfo = (number) => {
  if (!number) return null;
  const num = number.replace(/\D/g, '');

  for (const type in cardTypes) {
    for (const pattern of cardTypes[type].patterns) {
      if (pattern.test(num)) {
        return cardTypes[type];
      }
    }
  }

  return null;
};

export const getCardType = (number) => {
  const info = getCardInfo(number);
  return info ? info.type : null;
};

export const validateExpiryDate = (value) => {
  if (!value || value.length < 7) return false;

  const [monthStr, yearStr] = value.split(' / ');
  const month = parseInt(monthStr, 10);
  const year = parseInt(`20${yearStr}`, 10);

  if (isNaN(month) || isNaN(year) || month < 1 || month > 12) {
    return false;
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // getMonth() is 0-indexed

  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return false;
  }

  return true;
};

// // Luhn algorithm for card number validation
// export const isValidLuhn = (cardNumber) => {
//   const num = cardNumber.replace(/\s+/g, '');
//   if (!/^\d+$/.test(num)) return false;

//   let sum = 0;
//   let shouldDouble = false;
//   for (let i = num.length - 1; i >= 0; i--) {
//     let digit = parseInt(num.charAt(i), 10);

//     if (shouldDouble) {
//       if ((digit *= 2) > 9) digit -= 9;
//     }

//     sum += digit;
//     shouldDouble = !shouldDouble;
//   }
//   return (sum % 10) === 0;
// };
export const isValidLuhn = (cardNumber) => {
  const digits = cardNumber.replace(/\D/g, '').split('').map(Number);
  let sum = 0;
  let isEven = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = digits[i];
    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    isEven = !isEven;
  }
  return sum % 10 === 0;
};

export const statesArr = ['',
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];
export const statesOptions = [];
statesArr.forEach(element => {
  // let option = { label: (element ? element : 'Empty'), value: element };
  if (element) {
    let option = { label: element, value: element };
    statesOptions.push(option)
  }
  // statesOptions.push(option);
  // statesObj[element] = element;
})

export const isNumeric = function (str) {
  if (typeof str != "string") return false // we only process strings!  
  return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
}

export const formatMoneyValue = (value) => formatter.format(value)

export const loadExternalScript = function (src, attrs) { // eslint-disable-line no-param-reassign
  return new Promise(function (resolve, reject) {
    let shouldAppend = false;
    let element = document.querySelector('script[src="' + src + '"]');
    if (!element) {
      element = document.createElement('script');
      element.type = 'text/javascript';
      element.async = true;
      element.src = src;
      shouldAppend = true;

      for (let name in attrs) {
        if (name && attrs.hasOwnProperty(name)) {
          let value = attrs[name];
          if (value === true) {
            element.setAttribute(name, name);
          } else if (value !== false && value != null) {
            element.setAttribute(name, value.toString());
          }
        }
      }

    } else if (element.hasAttribute('data-loaded')) {
      resolve(element);
      return;
    }

    element.addEventListener('error', reject);
    element.addEventListener('abort', reject);
    element.addEventListener('load', () => {
      element.setAttribute('data-loaded', true);
      resolve(element);
    });

    if (shouldAppend) document.head.appendChild(element);
  });
}

export function appLog(message = '', ...args) {
  const prefix = '%c[DRPCS]%c';
  const style = ['font-weight: 700; color: #22996d;', 'font-weight: initial; color: initial'];
  console.log(`${prefix} ${message}`, ...style, ...args);
}